import CopilotBadge from '@github-ui/copilot-chat/components/CopilotBadge'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Box, RelativeTime, Text} from '@primer/react'
import type {PlaygroundMessage} from './model-client'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {useUser} from '@github-ui/use-user'

import {transformContentToHTML} from '@github-ui/copilot-chat/utils/markdown'
import {usePlaygroundManager} from './playground-manager'
import {PlaygroundError} from './PlaygroundError'
import {TokenLimitReachedResponseErrorDescription} from './playground-types'

export type PlaygroundChatMessageProps = {
  isLoading: boolean
  isError: boolean
  index: number
  message: PlaygroundMessage
}

export function PlaygroundChatMessage(props: PlaygroundChatMessageProps) {
  const message = props.message
  const index = props.index

  const htmlMessage = transformContentToHTML(message.message)

  const user = useUser()
  const manager = usePlaygroundManager()
  const determineMessageMeta = (_message: PlaygroundMessage) => {
    const model = manager.model

    switch (_message.role) {
      case 'error':
      case 'assistant':
        return {
          name: model.name,
          avatarUrl: model.logo_url,
        }
      case 'user':
        return {
          name: user.currentUser?.name ?? 'User',
          avatarUrl: user.currentUser?.avatarUrl ?? '/github.png',
        }
      default:
        return {
          name: 'default',
          avatarUrl: '/github.png',
        }
    }
  }

  const messageMeta = determineMessageMeta(message)

  return (
    <Box key={index} sx={{pb: 2}}>
      <Box
        className="message-container"
        data-testid="playground-chat-message"
        tabIndex={0}
        // ref={myRef}
        sx={{
          p: 1,
          // borderBottom: '1px solid var(--borderColor-muted, var(--color-border-muted))',
          fontSize: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontSize: 1,
          }}
        >
          <CopilotBadge
            isLoading={props.isLoading}
            isError={props.isError}
            customIcon={<GitHubAvatar src={messageMeta.avatarUrl} size={12} square={message.role !== 'user'} />}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <Box sx={{display: 'flex', gap: 2, alignItems: 'baseline'}}>
              <Text
                data-testid="chat-message-author-name"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {messageMeta.name}
              </Text>
              <Text
                sx={{
                  fontSize: 0,
                  fontWeight: 400,
                  color: 'fg.subtle',
                }}
              >
                {messageMeta.name !== 'user' && props.isLoading ? (
                  `Responding...`
                ) : (
                  <RelativeTime date={message.timestamp} format="relative" />
                )}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
      {props.isError ? (
        <PlaygroundError
          message={message.message}
          showResetButton={message.message === TokenLimitReachedResponseErrorDescription}
        />
      ) : (
        <Box sx={{p: 1}}>
          <MarkdownViewer verifiedHTML={htmlMessage} />
        </Box>
      )}
    </Box>
  )
}

try{ PlaygroundChatMessage.displayName ||= 'PlaygroundChatMessage' } catch {}